// МОРФ ПРОЗРАЧНОСТИ ПРИ СМЕНЕ РЕЖИМА ОТОБРАЖЕНИЯ — перенос из PRO100-онлайн.
// Донор: src/ui/display-mode-transition-controller.ts — `#fromOpacity`, `#startedAtMs`,
// `eased = 1 - (1-progress)^3`, длительность 280 мс, `reducedMotion` ставит результат сразу,
// и `depthWrite` возвращается не в начале перехода, а после его середины (донор: `progress >= .55`).
//
// Во вьюере «Каркас»/«Рентген»/«Плоско» переключались одним присваиванием: сцена не
// становилась прозрачной, она ОКАЗЫВАЛАСЬ прозрачной. Глазу негде увидеть, что именно
// изменилось, — а при «рентгене» меняется вся сцена разом, и без перехода это читается как
// подмена картинки, а не как смена взгляда на ту же.
//
// ЗДЕСЬ ЖИВЁТ ТОЛЬКО ВРЕМЯ. Какие числа у материала в каждом режиме, решает контракт объекта
// (`FurnitureEntityVisibility.materialStateForMode`), и второй руки на материалах не заводится:
// доля перехода уходит В ТОГО ЖЕ владельца, который и так красит материалы
// (`FurnitureRenderObject.applyMaterialMode`). Кривая тоже не своя — общая `easeOutCubic`.

import type { RenderMode } from '@/shared/store/viewer-store';
import { easeOutCubic } from './moduleFeedbackAnimation';

/** Донорская длительность (display-mode-transition-controller.ts, `durationMs = 280`). */
export const RENDER_MODE_MORPH_DURATION_MS = 280;

/**
 * Доля, после которой возвращается запись глубины. Раньше середины включать нельзя: пока обе
 * стороны перехода полупрозрачны, запись глубины съела бы то, что должно просвечивать.
 * Донор: `depthWrite: this.#mode !== 'xray' && progress >= .55`.
 */
export const RENDER_MODE_DEPTH_WRITE_AT = 0.55;

export type RenderModeTween = Readonly<{ mode: RenderMode; startedMs: number }>;

export type RenderModeMorph = Readonly<{
  mode: RenderMode;
  /** Доля перехода со сглаживанием: 1 — переход окончен. */
  progress: number;
  /** Момент начала — метка перехода: по ней владелец материалов понимает, что начался НОВЫЙ. */
  startedMs: number;
  done: boolean;
}>;

export function renderModeAtRest(mode: RenderMode): RenderModeTween {
  return Object.freeze({ mode, startedMs: Number.NEGATIVE_INFINITY });
}

/**
 * Новый режим.
 *
 * `reducedMotion` — не «быстрее», а «уже на месте»: метка `-Infinity` делает переход
 * завершённым на первом же кадре, ровно как донорское `active = !reducedMotion`.
 */
export function retargetRenderMode(
  current: RenderModeTween,
  mode: RenderMode,
  nowMs: number,
  reducedMotion: boolean,
): RenderModeTween {
  if (current.mode === mode) return current;
  if (reducedMotion) return renderModeAtRest(mode);
  return Object.freeze({ mode, startedMs: nowMs });
}

export function sampleRenderMode(tween: RenderModeTween, nowMs: number): RenderModeMorph {
  const raw = (nowMs - tween.startedMs) / RENDER_MODE_MORPH_DURATION_MS;
  if (!(raw < 1)) return Object.freeze({ mode: tween.mode, progress: 1, startedMs: tween.startedMs, done: true });
  const progress = raw <= 0 ? 0 : easeOutCubic(raw);
  return Object.freeze({ mode: tween.mode, progress, startedMs: tween.startedMs, done: false });
}
